import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { useAuth } from "@core/auth/AuthProvider";
import { AppShell } from "@core/layout/AppShell";
import { SettingsLayout } from "@core/layout/SettingsLayout";
import { getModuleRoutes } from "@core/module-registry";
import { AppearanceSettingsPage } from "@core/pages/settings/AppearanceSettingsPage";
import { BusinessSettingsPage } from "@core/pages/settings/BusinessSettingsPage";
import { ModulesSettingsPage } from "@core/pages/settings/ModulesSettingsPage";
import { ProfileSettingsPage } from "@core/pages/settings/ProfileSettingsPage";
import { SecuritySettingsPage } from "@core/pages/settings/SecuritySettingsPage";
import { SettingsIndexPage } from "@core/pages/settings/SettingsIndexPage";
import { LoginPage } from "@core/pages/LoginPage";

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

function IndexRedirector() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user) return <Navigate to="/jobs" replace />;
  return <Navigate to="/login" replace />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexRedirector,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => <LoginPage initialMode="login" />,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: () => <LoginPage initialMode="signup" />,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsLayout,
});

function settingsChild(path, component) {
  return createRoute({ getParentRoute: () => settingsRoute, path, component });
}

const settingsIndexRoute = settingsChild("/", SettingsIndexPage);
const settingsProfileRoute = settingsChild("/profile", ProfileSettingsPage);
const settingsBusinessRoute = settingsChild("/business", BusinessSettingsPage);
const settingsSidebarRoute = settingsChild("/sidebar", AppearanceSettingsPage);
const settingsAppearanceLegacyRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/appearance",
  beforeLoad: () => {
    throw redirect({ to: "/settings/sidebar" });
  },
});
const settingsModulesRoute = settingsChild("/modules", ModulesSettingsPage);
const settingsSecurityRoute = settingsChild("/security", SecuritySettingsPage);

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  settingsRoute.addChildren([
    settingsIndexRoute,
    settingsProfileRoute,
    settingsBusinessRoute,
    settingsSidebarRoute,
    settingsAppearanceLegacyRoute,
    settingsModulesRoute,
    settingsSecurityRoute,
  ]),
  ...getModuleRoutes(rootRoute),
]);

export const router = createRouter({ routeTree });

